import React from 'react';
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { tokens } from '../../../theme';

export const MotionBox = motion.create(Box);

export const editorialSerif = '"Iowan Old Style", "Palatino Linotype", "Book Antiqua", Georgia, serif';

export const analyticsTokens = {
  panel: 'rgba(15, 24, 19, 0.92)',
  panelSoft: 'rgba(18, 29, 23, 0.82)',
  border: 'rgba(197, 215, 152, 0.16)',
  borderStrong: 'rgba(197, 215, 152, 0.28)',
};

export const chartTooltipStyle = {
  backgroundColor: '#111915',
  border: `1px solid ${analyticsTokens.border}`,
  color: tokens.cream,
  borderRadius: 0,
};

export function panelSx(extra = {}) {
  return {
    backgroundColor: analyticsTokens.panel,
    borderRadius: 0,
    border: `1px solid ${analyticsTokens.border}`,
    ...extra,
  };
}

export function AnalyticsHero({ title, subtitle, backgroundImage, actions, chips = [] }) {
  return (
    <Box
      sx={{
        p: { xs: 2.5, md: 3 },
        mb: 2,
        border: `1px solid ${analyticsTokens.border}`,
        backgroundImage: backgroundImage
          ? `linear-gradient(180deg, rgba(7,16,13,0.28), rgba(7,16,13,0.68)), url(${backgroundImage})`
          : `linear-gradient(180deg, rgba(7,16,13,0.42), rgba(7,16,13,0.74))`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }}>
        <Box>
          <Typography variant="h2" sx={{ color: tokens.cream, mb: 0.75, fontFamily: editorialSerif, fontWeight: 500, letterSpacing: '-0.02em', fontSize: { xs: '2rem', md: '2.45rem' } }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body1" sx={{ color: tokens.textSoft, maxWidth: 680 }}>
              {subtitle}
            </Typography>
          )}
          {chips.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
              {chips.map((chip) => (
                <Chip key={chip} label={chip} size="small" sx={{ borderRadius: 2, border: `1px solid ${analyticsTokens.border}`, color: tokens.textSoft }} />
              ))}
            </Stack>
          )}
        </Box>
        {actions}
      </Stack>
    </Box>
  );
}

export function MetricTile({ label, value, footnote, accent = tokens.accentStrong, icon }) {
  return (
    <Card sx={panelSx({ height: '100%' })}>
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          {icon && (
            <Box sx={{ width: 38, height: 38, display: 'grid', placeItems: 'center', border: `1px solid ${analyticsTokens.borderStrong}`, color: accent, backgroundColor: 'rgba(255,255,255,0.02)' }}>
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ color: tokens.textMuted }}>
              {label}
            </Typography>
            <Typography variant="h4" sx={{ color: tokens.cream, mt: 0.35 }}>
              {value}
            </Typography>
            {footnote && (
              <Typography variant="body2" sx={{ color: tokens.textSoft, mt: 0.35 }}>
                {footnote}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function ChartPanel({ title, subtitle, children, height = 280, action }) {
  return (
    <Card sx={panelSx()}>
      <CardContent sx={{ p: { xs: 2, md: 2.25 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ color: tokens.cream, fontFamily: editorialSerif, fontWeight: 500 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: tokens.textMuted, mt: 0.35 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
        <Box sx={{ height }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

export function InsightCallout({ title, body, tone = tokens.accentStrong }) {
  return (
    <Box sx={{ p: 1.6, border: `1px solid ${analyticsTokens.border}`, borderLeft: `3px solid ${tone}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>
      <Typography variant="body2" sx={{ color: tone, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: tokens.textSoft, lineHeight: 1.6 }}>
        {body}
      </Typography>
    </Box>
  );
}

export function AnalyticsScreen({ children }) {
  return (
    <MotionBox initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      {children}
    </MotionBox>
  );
}
